import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMenu, FiX, FiInstagram } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { SQUARE_SERVICES_URL } from '../constants/links';
import logoBlack from '../images/The lashroom logo/blacklogo.svg';
import { useLanguage } from '../i18n/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const navLinks = [
    { name: t('nav.services'), href: '#services' },
    { name: t('nav.about'), href: '#about' },
    { name: t('nav.reviews'), href: '#reviews' },
    { name: t('nav.location'), href: '#location' },
    { name: t('nav.faq'), href: '#faq' },
  ];

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || isMobileMenuOpen ? 'bg-brand-cream shadow-sm py-4' : 'bg-transparent py-6'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex justify-between items-center relative z-50">
        <a 
          href="#" 
          className="flex items-center gap-3"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-label="The Lash Room"
        >
          <img src={logoBlack} alt="The Lash Room" className="h-9 md:h-10 w-auto" />
        </a>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a 
              key={link.name} 
              href={link.href}
              className="text-sm tracking-widest uppercase text-brand-muted hover:text-brand-taupe transition-colors"
            >
              {link.name}
            </a>
          ))}
          <a 
            href="#" 
            className="text-brand-dark hover:text-brand-taupe transition-colors"
            aria-label={t('nav.instagram')}
          >
            <SafeIcon icon={FiInstagram} className="w-5 h-5" />
          </a>
          <LanguageSwitcher />
          <a 
            href={SQUARE_SERVICES_URL}
            target="_blank"
            rel="noreferrer"
            className="bg-brand-taupe hover:bg-brand-taupeDark text-white px-6 py-2.5 rounded-full text-sm font-medium transition-colors shadow-sm"
          >
            {t('nav.bookNow')}
          </a>
        </nav>

        {/* Mobile Menu Toggle */}
        <button 
          className="md:hidden text-brand-dark p-2 -mr-2"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle Menu"
        >
          <SafeIcon icon={isMobileMenuOpen ? FiX : FiMenu} className="w-6 h-6" />
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-brand-cream z-40 flex flex-col justify-center items-center px-6 h-[100dvh] w-screen"
          >
            <nav className="flex flex-col items-center gap-8">
              {navLinks.map((link, index) => (
                <motion.a 
                  key={link.name} 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + index * 0.1 }}
                  href={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="font-serif text-3xl text-brand-dark"
                >
                  {link.name}
                </motion.a>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="flex flex-col items-center gap-6 mt-8 w-full max-w-xs"
              >
                <a 
                  href={SQUARE_SERVICES_URL}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="bg-brand-taupe text-white px-8 py-4 rounded-full text-lg w-full text-center shadow-lg"
                >
                  {t('nav.bookAppointment')}
                </a>
                <div className="w-full flex justify-center">
                  <LanguageSwitcher />
                </div>
                <a 
                  href="#" 
                  className="text-brand-muted flex items-center gap-2 uppercase tracking-widest text-sm"
                  aria-label={t('nav.instagram')}
                >
                  <SafeIcon icon={FiInstagram} className="w-5 h-5" />
                  <span>{t('nav.instagram')}</span>
                </a>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
